import { useMemo, useState } from "react";

import clsx from "clsx";
import { saveAs } from "file-saver";
import FuzzySearch from "fuzzy-search";
import { first, groupBy, orderBy, sumBy } from "lodash";
import Papa from "papaparse";

import RegularGraphic from "@assets/page-loading.gif";
import { useUser } from "@components/user-context";
import { TertiaryButton } from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import {
  makeAOV,
  makeASP,
  makeCac,
  makeRoas,
} from "@north-beam/nb-client-side/metrics";
import {
  type FetchProductAnalytics,
  type FetchProductAnalyticsByAd,
  type FetchProductAnalyticsByAdVariables,
  type FetchProductAnalyticsByCampaign,
  type FetchProductAnalyticsByCampaignVariables,
  type FetchProductAnalyticsByPlatform,
  type FetchProductAnalyticsByPlatformVariables,
  type FetchProductAnalyticsRankings,
  type FetchProductAnalyticsRankingsVariables,
  type FetchProductAnalyticsVariables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { formatting, todayLocalAsISODate } from "@north-beam/nb-common";
import { FeedbackForm } from "@shared/feedback-form";
import { parseDateRangeArgument } from "@utils/index";

import BubbleChart, { transformGraphData } from "./components/bubble-chart";
import { ChartControl } from "./components/chart-controls";
import FilterModal from "./components/filter-modal";
import TabTable, { getColumns } from "./components/table/tab-table";
import { useProductAnalyticsContext } from "./hooks";
import {
  assignPercentages,
  filterDataByColumnAndOperation,
  platformIdsForQuery,
  quickSelectOptions,
  rollUpMetricRows,
} from "./metricsData";
import {
  FETCH_PRODUCT_ANALYTICS,
  FETCH_PRODUCT_ANALYTICS_BY_AD,
  FETCH_PRODUCT_ANALYTICS_BY_CAMPAIGN,
  FETCH_PRODUCT_ANALYTICS_BY_PLATFORM,
  FETCH_PRODUCT_ANALYTICS_RANKINGS,
} from "./queries";
import { Tabs } from "./tabs";

export default function ProductAnalyticsPage() {
  const {
    filters,
    setSorting,
    tabState,
    setSelectedProducts,
    tableState,
    setSelectedPlatforms,
    setSelectedCampaigns,
    setSelectedAds,
    setQuickSelect,
    selectedQuickSelect,
    setAdvancedSearch,
  } = useProductAnalyticsContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dateRange = parseDateRangeArgument(
    filters.dateRange,
    todayLocalAsISODate(),
  );

  const { user } = useUser();
  const prettyLogEventPage = usePrettyLogEventPage();

  const { hasFeatureFlag } = useFeatureFlags();

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const isScrollable =
    (document.querySelector(".overflow-scrollable-div")?.scrollWidth || 0) >
    (document.querySelector(".overflow-scrollable-div")?.clientWidth || 0);

  const [showRightScroll, setShowRightScroll] = useState<boolean>(true);

  const {
    data: productAnalyticsRankings,
    loading: productAnalyticsRankingsLoading,
  } = useNorthbeamQuery<
    FetchProductAnalyticsRankings,
    FetchProductAnalyticsRankingsVariables
  >(FETCH_PRODUCT_ANALYTICS_RANKINGS, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    skip: tabState.tabView !== "category",
    variables: {
      filter: {
        dateRange: {
          start: dateRange.startDate,
          end: dateRange.endDate,
        },
        accountingMode: filters.accountingMode,
        attributionModel: filters.attributionModel,
        attributionWindow: filters.attributionWindow,
      },
    },
  });

  const { data, loading } = useNorthbeamQuery<
    FetchProductAnalytics,
    FetchProductAnalyticsVariables
  >(FETCH_PRODUCT_ANALYTICS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        dateRange: {
          start: dateRange.startDate,
          end: dateRange.endDate,
        },
        accountingMode: filters.accountingMode,
        attributionModel: filters.attributionModel,
        attributionWindow: filters.attributionWindow,
      },
    },
  });

  const { data: byPlatformData, loading: byPlatformLoading } =
    useNorthbeamQuery<
      FetchProductAnalyticsByPlatform,
      FetchProductAnalyticsByPlatformVariables
    >(FETCH_PRODUCT_ANALYTICS_BY_PLATFORM, {
      notifyOnNetworkStatusChange: true,
      variables: {
        filter: {
          dateRange: {
            start: dateRange.startDate,
            end: dateRange.endDate,
          },
          accountingMode: filters.accountingMode,
          attributionModel: filters.attributionModel,
          attributionWindow: filters.attributionWindow,
          productIds:
            tableState.selectedProducts.length > 0
              ? tableState.selectedProducts
              : [],
        },
      },
    });
  const { data: byAdData, loading: byAdLoading } = useNorthbeamQuery<
    FetchProductAnalyticsByAd,
    FetchProductAnalyticsByAdVariables
  >(FETCH_PRODUCT_ANALYTICS_BY_AD, {
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        dateRange: {
          start: dateRange.startDate,
          end: dateRange.endDate,
        },
        accountingMode: filters.accountingMode,
        attributionModel: filters.attributionModel,
        attributionWindow: filters.attributionWindow,
        campaignIds:
          tableState.selectedCampaigns.length > 0
            ? tableState.selectedCampaigns.map((c) => c.split(":")[0])
            : [],
        platformIds:
          tableState.selectedPlatforms.length > 0
            ? platformIdsForQuery(tableState?.selectedPlatforms)
            : [],
        productIds:
          tableState.selectedProducts.length > 0
            ? tableState.selectedProducts
            : [],
      },
    },
  });

  const { data: byCampaignData, loading: byCampaignLoading } =
    useNorthbeamQuery<
      FetchProductAnalyticsByCampaign,
      FetchProductAnalyticsByCampaignVariables
    >(FETCH_PRODUCT_ANALYTICS_BY_CAMPAIGN, {
      notifyOnNetworkStatusChange: true,
      variables: {
        filter: {
          dateRange: {
            start: dateRange.startDate,
            end: dateRange.endDate,
          },
          accountingMode: filters.accountingMode,
          attributionModel: filters.attributionModel,
          attributionWindow: filters.attributionWindow,
          platformIds:
            tableState.selectedPlatforms.length > 0
              ? platformIdsForQuery(tableState?.selectedPlatforms)
              : [],
          productIds:
            tableState.selectedProducts.length > 0
              ? tableState.selectedProducts
              : [],
        },
      },
    });

  const cacMetric = makeCac(
    filters.attributionModel,
    filters.attributionWindow,
    "",
    "",
    filters.accountingMode,
  );
  const ROASMetric = makeRoas(
    filters.attributionModel,
    filters.attributionWindow,
    "",
    "",
    filters.accountingMode,
  );
  const AOVMetric = makeAOV(
    filters.attributionModel,
    filters.attributionWindow,
    "",
    "",
    filters.accountingMode,
  );

  const ASPMetric = makeASP(
    filters.attributionModel,
    filters.attributionWindow,
    "",
    "",
    filters.accountingMode,
  );

  const groupedCategories = useMemo(
    () =>
      groupBy(
        productAnalyticsRankings?.me?.productAnalyticRankings?.summary || [],
        "collection_id",
      ),
    [productAnalyticsRankings?.me?.productAnalyticRankings?.summary],
  );

  const categoriesWithMetrics = useMemo(
    () =>
      assignPercentages(
        rollUpMetricRows({
          rows: Object.keys(groupedCategories)?.map((categoryId) => {
            return {
              id: categoryId,
              rev: sumBy(groupedCategories[categoryId], "rev") || 0,
              spend: sumBy(groupedCategories[categoryId], "spend") || 0,
              name: first(groupedCategories[categoryId])?.collection_name || "",
              quantity: sumBy(groupedCategories[categoryId], "quantity") || 0,
              rev_returning:
                sumBy(
                  groupedCategories[categoryId],
                  "cust_stage_returning_rev",
                ) || 0,
              rev_first_time:
                sumBy(
                  groupedCategories[categoryId],
                  "cust_stage_first_time_rev",
                ) || 0,
              txns: sumBy(groupedCategories[categoryId], "txns") || 0,
            };
          }),
          attributionModel: filters.attributionModel,
          attributionWindow: filters.attributionWindow,
          cacMetric,
          ROASMetric,
          AOVMetric,
          ASPMetric,
        }),
      ),
    [
      AOVMetric,
      ASPMetric,
      ROASMetric,
      cacMetric,
      filters.attributionModel,
      filters.attributionWindow,
      groupedCategories,
    ],
  );
  let filteredRows = data?.me?.productAnalytics?.summary;
  if (
    tableState.metricFilters.length !== 0 &&
    (data?.me?.productAnalytics?.summary?.length || 0) > 0
  ) {
    filteredRows = tableState.metricFilters.reduce(
      (inProgressFilteredRows, filter) => {
        return filterDataByColumnAndOperation(
          filter.value,
          inProgressFilteredRows || [],
          filter.operator,
          filter.id,
        );
      },
      data?.me?.productAnalytics?.summary,
    );
  }
  const productsWithMetrics = useMemo(
    () =>
      assignPercentages(
        rollUpMetricRows({
          rows:
            filteredRows?.map((product) => {
              return {
                ...product,
                id: product?.product_id || "",
                rev: product?.rev || 0,
                spend: product?.spend || 0,
                quantity: product?.quantity || 0,
                name: product?.product_title_latest || "",
                rev_returning: product?.returning_total_revenue || 0,
                rev_first_time: product?.total_revenue_first_time || 0,
                proportional_spend: product?.proportional_spend || 0,
                order_rev: product.order_rev || 0,
                txns: product?.txns || 0,
              };
            }) || [],
          attributionModel: filters.attributionModel,
          attributionWindow: filters.attributionWindow,
          cacMetric,
          ROASMetric,
          AOVMetric,
          ASPMetric,
        }),
      ),
    [
      AOVMetric,
      ASPMetric,
      ROASMetric,
      cacMetric,
      filteredRows,
      filters.attributionModel,
      filters.attributionWindow,
    ],
  );

  const platformWithMetrics = useMemo(
    () =>
      assignPercentages(
        rollUpMetricRows({
          rows:
            byPlatformData?.me?.productAnalyticsByPlatform?.summary?.map(
              (platform) => {
                return {
                  ...platform,
                  id:
                    [
                      ...(platform?.platforms || ""),
                      platform.platform_label,
                    ]?.join(";") || "",
                  rev: platform.rev || 0,
                  spend: platform.spend || 0,
                  quantity: platform.quantity || 0,
                  name: platform?.platform_label || "",
                  rev_returning: platform.returning_total_revenue || 0,
                  rev_first_time: platform.total_revenue_first_time || 0,
                  proportional_spend: platform.proportional_spend || 0,
                  order_rev: platform.order_rev || 0,
                  txns: platform.txns || 0,
                };
              },
            ) || [],
          attributionModel: filters.attributionModel,
          attributionWindow: filters.attributionWindow,
          cacMetric,
          ROASMetric,
          AOVMetric,
          ASPMetric,
        }),
      ),
    [
      AOVMetric,
      ASPMetric,
      ROASMetric,
      byPlatformData?.me?.productAnalyticsByPlatform?.summary,
      cacMetric,
      filters.attributionModel,
      filters.attributionWindow,
    ],
  );

  const adsWithMetrics = useMemo(
    () =>
      assignPercentages(
        rollUpMetricRows({
          rows:
            byAdData?.me?.productAnalyticsByAd?.summary?.map((ad, i) => {
              return {
                ...ad,
                id: `${ad.ad || ""}:${ad.ad_name}:${i}` || "",
                rev: ad.rev || 0,
                spend: ad.spend || 0,
                name: ad.ad_name || "",
                quantity: ad.quantity || 0,
                rev_returning: ad.returning_total_revenue || 0,
                rev_first_time: ad.total_revenue_first_time || 0,
                txns: ad.txns || 0,
                proportional_spend: ad.proportional_spend || 0,
                order_rev: ad.order_rev || 0,
                campaign: ad.campaign || "",
                campaign_name: ad.campaign_name || "",
                platform: ad.platform || "",
              };
            }) || [],
          attributionModel: filters.attributionModel,
          attributionWindow: filters.attributionWindow,
          cacMetric,
          ROASMetric,
          AOVMetric,
          ASPMetric,
        }),
      ),
    [
      AOVMetric,
      ASPMetric,
      ROASMetric,
      byAdData?.me?.productAnalyticsByAd?.summary,
      cacMetric,
      filters.attributionModel,
      filters.attributionWindow,
    ],
  );

  const campaignsWithMetrics = useMemo(
    () =>
      assignPercentages(
        rollUpMetricRows({
          rows:
            byCampaignData?.me?.productAnalyticsByCampaign?.summary?.map(
              (campaign, i) => {
                return {
                  ...campaign,
                  id:
                    `${campaign.campaign || ""}:${
                      campaign.campaign_name
                    }:${i}` || "",
                  rev: campaign.rev || 0,
                  spend: campaign.spend || 0,
                  name: campaign.campaign_name || "",
                  quantity: campaign.quantity || 0,
                  rev_returning: campaign.returning_total_revenue || 0,
                  rev_first_time: campaign.total_revenue_first_time || 0,
                  proportional_spend: campaign.proportional_spend || 0,
                  order_rev: campaign.order_rev || 0,
                  txns: campaign.txns || 0,
                  platform: campaign.platform || "",
                };
              },
            ) || [],
          attributionModel: filters.attributionModel,
          attributionWindow: filters.attributionWindow,
          cacMetric,
          ROASMetric,
          AOVMetric,
          ASPMetric,
        }),
      ),
    [
      AOVMetric,
      ASPMetric,
      ROASMetric,
      byCampaignData?.me?.productAnalyticsByCampaign?.summary,
      cacMetric,
      filters.attributionModel,
      filters.attributionWindow,
    ],
  );

  const searchInput = () => {
    switch (tabState.tabView) {
      case "category":
        return new FuzzySearch(categoriesWithMetrics || [], ["name", "id"]);
      case "product":
        return new FuzzySearch(
          selectedCategories.length > 0
            ? productsWithMetrics.filter((productElement) =>
                tableState.selectedProducts.includes(productElement.id || ""),
              ) || []
            : productsWithMetrics || [],
          ["name", "id"],
        );
      case "platform":
        return new FuzzySearch(platformWithMetrics || [], ["name", "id"]);
      case "campaign":
        return new FuzzySearch(campaignsWithMetrics || [], ["name", "id"]);
      case "ad":
        return new FuzzySearch(adsWithMetrics || [], ["name", "id"]);

      default:
        return null;
    }
  };

  const searcher = searchInput();

  const categoryBarDataSet = orderBy(categoriesWithMetrics, ["rev"], ["desc"]);

  const productAnalyticsDataset = orderBy(
    productsWithMetrics,
    ["rev"],
    ["desc"],
  );

  const productAnalyticsPlatformDataset = orderBy(
    platformWithMetrics,
    ["rev"],
    ["desc"],
  );

  const adBarDataset = orderBy(adsWithMetrics, ["rev"], ["desc"]);

  const graphDataInput = () => {
    switch (tabState.tabView) {
      case "category":
        return selectedCategories.length > 0
          ? categoryBarDataSet.filter((category) =>
              selectedCategories.includes(category.id),
            )
          : categoryBarDataSet;
      case "product":
        return tableState.selectedProducts.length > 0
          ? productAnalyticsDataset.filter((product) =>
              tableState.selectedProducts.includes(product.id),
            )
          : productAnalyticsDataset;
      case "campaign":
        return tableState.selectedCampaigns.length > 0
          ? campaignsWithMetrics.filter((campaign) =>
              tableState.selectedCampaigns.includes(campaign.id),
            )
          : campaignsWithMetrics;
      case "ad":
        return tableState.selectedAds.length > 0
          ? adBarDataset.filter((ad) => tableState.selectedAds.includes(ad.id))
          : adBarDataset;
      default:
        return tableState.selectedPlatforms.length > 0
          ? productAnalyticsPlatformDataset.filter((platform) =>
              tableState.selectedPlatforms.includes(platform.id),
            )
          : productAnalyticsPlatformDataset;
    }
  };

  return (
    <div className="bg-v2-primary z-1 relative">
      <ChartControl />
      <div className="px-6">
        <div className="relative m-auto p-4 h-[560px] rounded-lg mb-6 border border-v2-tertiary">
          <h1 className="text-v2-primary text-[16px] font-semibold mb-0">
            Product Performance
          </h1>
          <div className="relative w-full h-[512px] overflow-hidden -mt-4">
            <BubbleChart
              datasets={transformGraphData(
                graphDataInput(),
                formatting,
                user,
                tableState.metricFilters,
              )}
            />
          </div>
        </div>
        <Tabs
          clearCategorySelected={() => setSelectedCategories([])}
          selectedCategoryCount={selectedCategories.length}
        />
        {!hasFeatureFlag("enableDashboardV2") ? (
          <div className="flex justify-center items-center">
            {isScrollable && isScrolled && (
              <div className="flex justify-end w-[32px] h-[32px]">
                <button
                  className="w-full"
                  onClick={() => {
                    const div = document.querySelector(
                      ".overflow-scrollable-div",
                    );
                    if (div) {
                      div.scrollLeft = 0;
                    }
                    setShowRightScroll(true);
                    setIsScrolled(false);
                  }}
                >
                  <i className="fa-solid fa-chevron-left text-primary"></i>
                </button>
              </div>
            )}
            <div className="flex gap-2 w-full my-6 overflow-scrollable-div overflow-hidden items-center">
              {quickSelectOptions({
                setSorting,
                setSelectedQuickSelect: setQuickSelect,
              }).map((option) => (
                <button
                  key={option.id}
                  className={clsx(
                    "button bg-v2-primary secondary-button md flex items-center gap-3 h-9 px-4 rounded  hover:bg-[#F9FAFF] text-left",
                    "text-v2-primary font-tt-commons-pro text-xs font-semibold leading-4 tracking-wider uppercase ",
                    option.id === selectedQuickSelect.quickSelect
                      ? "border-cosmic bg-primary text-white"
                      : "",
                  )}
                  onClick={() => {
                    if (selectedQuickSelect.quickSelect === option.id) {
                      setQuickSelect("");
                      setSelectedProducts([]);
                      setSelectedPlatforms([]);
                      setSelectedCampaigns([]);
                      setSelectedAds([]);
                    } else {
                      setSelectedProducts([]);
                      setSelectedPlatforms([]);
                      setSelectedCampaigns([]);
                      setSelectedAds([]);
                      prettyLogEventPage({
                        domain: "Product Analytics",
                        action: "onClick",
                        additionalActionInfo: `Quick select clicked ${option.name}`,
                      });
                      option.onClick();
                    }
                  }}
                >
                  <span className="leading-4">{option.name}</span>
                </button>
              ))}
              {(document.querySelector(".overflow-scrollable-div")
                ?.scrollWidth || 0) >
                (document.querySelector(".overflow-scrollable-div")
                  ?.clientWidth || 0) &&
                showRightScroll && (
                  <div className="flex justify-end w-[32px] h-[32px]">
                    <button
                      className="w-full"
                      onClick={() => {
                        const div = document.querySelector(
                          ".overflow-scrollable-div",
                        );
                        if (div) {
                          div.scrollLeft = div.scrollWidth;

                          if (
                            div.scrollLeft ===
                            div.scrollWidth - div.clientWidth
                          ) {
                            setShowRightScroll(false);
                          }
                        }
                        setIsScrolled(true);
                      }}
                    >
                      <i className="fa-solid fa-chevron-right text-primary"></i>
                    </button>
                  </div>
                )}
            </div>
          </div>
        ) : (
          <div className="mb-4"></div>
        )}
        <div className="flex mb-3 w-full justify-between">
          <div className="flex flex-row gap-2">
            <input
              type="text"
              className="text-sm font-normal py-[5px] px-2
              placeholder:font-light mr-1 bg-v2-primary border-v2-secondary hover:border-v2-primary border rounded-md"
              placeholder="Search..."
              value={filters.advancedSearch}
              onChange={(e) => {
                setAdvancedSearch(e.currentTarget.value);
              }}
            />
            {tableState.selectedProducts.length > 0 && (
              <div
                className="flex pl-1 pr-2 justify-center items-center gap-1 rounded-2xl
             border border-v2-notice bg-v2-highlight-notice text-v2-action-positive text-[10px] font-bold uppercase"
              >
                <div
                  className="flex w-6 h-6 min-w-[24px] flex-col
              justify-center items-center gap-2 rounded-[16px] bg-v2-primary shadow-[0px_0px_2px_0px_rgba(0,0,0,0.10)]"
                >
                  {tableState.selectedProducts.length}
                </div>
                Products
                <i
                  onClick={() => {
                    setQuickSelect("");
                    setSelectedProducts([]);
                    setSelectedPlatforms([]);
                    setSelectedCampaigns([]);
                    setSelectedAds([]);
                  }}
                  className="fa fa-close text-v2-action-positive text-[12px] font-[900] cursor-pointer"
                ></i>
              </div>
            )}
            {tableState.selectedPlatforms.length > 0 && (
              <div
                className="flex pl-1 pr-2 justify-center items-center gap-1 rounded-2xl
             border border-v2-notice bg-v2-highlight-notice text-v2-action-positive text-[10px] font-bold uppercase"
              >
                <div
                  className="flex w-6 h-6 min-w-[24px] flex-col
              justify-center items-center gap-2 rounded-[16px] bg-v2-primary shadow-[0px_0px_2px_0px_rgba(0,0,0,0.10)]"
                >
                  {tableState.selectedPlatforms.length}
                </div>
                Platforms
                <i
                  onClick={() => {
                    setQuickSelect("");
                    setSelectedPlatforms([]);
                    setSelectedCampaigns([]);
                    setSelectedAds([]);
                  }}
                  className="fa fa-close text-v2-action-positive text-[12px] font-[900] cursor-pointer"
                ></i>
              </div>
            )}
            {tableState.selectedCampaigns.length > 0 && (
              <div
                className="flex pl-1 pr-2 justify-center items-center gap-1 rounded-2xl
             border border-v2-notice bg-v2-highlight-notice text-v2-action-positive text-[10px] font-bold uppercase"
              >
                <div
                  className="flex w-6 h-6 min-w-[24px] flex-col
              justify-center items-center gap-2 rounded-[16px] bg-v2-primary shadow-[0px_0px_2px_0px_rgba(0,0,0,0.10)]"
                >
                  {tableState.selectedCampaigns.length}
                </div>
                Campaigns
                <i
                  onClick={() => {
                    setQuickSelect("");
                    setSelectedCampaigns([]);
                    setSelectedAds([]);
                  }}
                  className="fa fa-close text-v2-action-positive text-[12px] font-[900] cursor-pointer"
                ></i>
              </div>
            )}
            {tableState.selectedAds.length > 0 && (
              <div
                className="flex pl-1 pr-2 justify-center items-center gap-1 rounded-2xl
             border border-v2-notice bg-v2-highlight-notice text-v2-action-positive text-[10px] font-bold uppercase"
              >
                <div
                  className="flex w-6 h-6 min-w-[24px] flex-col
              justify-center items-center gap-2 rounded-[16px] bg-v2-primary shadow-[0px_0px_2px_0px_rgba(0,0,0,0.10)]"
                >
                  {tableState.selectedAds.length}
                </div>
                Ads
                <i
                  onClick={() => {
                    setQuickSelect("");
                    setSelectedAds([]);
                  }}
                  className="fa fa-close text-v2-action-positive text-[12px] font-[900] cursor-pointer"
                ></i>
              </div>
            )}
          </div>
          <div className="flex flex-row gap-2">
            <TertiaryButton
              onClick={() => {
                const metricRows =
                  searcher?.search(filters.advancedSearch) || [];
                let startingColumnTitle = "";
                switch (tabState.tabView) {
                  case "product":
                    startingColumnTitle = "product";
                    break;
                  case "platform":
                    startingColumnTitle = "platform";
                    break;
                  case "campaign":
                    startingColumnTitle = "campaign";
                    break;
                  case "ad":
                    startingColumnTitle = "ad";
                    break;
                }
                const columns = getColumns(
                  startingColumnTitle,
                  tabState.tabView,
                );

                if (metricRows.length === 0) {
                  return;
                }
                const csvColumns = columns.map((c) => c.name);
                const csvRows = metricRows.map((row: any) =>
                  csvColumns.map((columnName) => row[columnName as any]),
                );
                const csvString = Papa.unparse([csvColumns, ...csvRows]);

                const blob = new Blob([csvString], {
                  type: "text/plain;charset=utf-8",
                });

                saveAs(blob, `tab-${tabState.tabView}-product-analytics.csv`);
              }}
            >
              EXPORT
            </TertiaryButton>
            <TertiaryButton
              onClick={() => setIsModalOpen(true)}
              className="flex gap-2 items-center justify-center"
            >
              FILTER
              <i className="fa-regular fa-sharp fa-filter" />
            </TertiaryButton>
          </div>
        </div>
        <div className="relative bg-v2-primary mb-16">
          {tabState.tabView === "category" && (
            <>
              {productAnalyticsRankingsLoading && (
                <div className="h-[600px]">
                  <img
                    src={RegularGraphic}
                    alt="Loading screen gif"
                    className={clsx("w-1/2 m-auto")}
                  />
                </div>
              )}
              {productAnalyticsRankings && data && (
                <TabTable
                  startingColumnTitle="Name"
                  metricRows={searcher?.search(filters.advancedSearch) || []}
                  setSelectedItems={setSelectedCategories}
                  selectedItems={selectedCategories}
                />
              )}
            </>
          )}
          {tabState.tabView === "product" && (
            <>
              {loading && (
                <div className="h-[600px]">
                  <img
                    src={RegularGraphic}
                    alt="Loading screen gif"
                    className={clsx("w-1/2 m-auto")}
                  />
                </div>
              )}
              {data && (
                <TabTable
                  startingColumnTitle="Product Name"
                  metricRows={searcher?.search(filters.advancedSearch) || []}
                  setSelectedItems={setSelectedProducts}
                  selectedItems={tableState.selectedProducts}
                />
              )}
            </>
          )}
          {tabState.tabView === "platform" && (
            <>
              {byPlatformLoading && (
                <div className="h-[600px]">
                  <img
                    src={RegularGraphic}
                    alt="Loading screen gif"
                    className={clsx("w-1/2 m-auto")}
                  />
                </div>
              )}
              {byPlatformData && (
                <TabTable
                  startingColumnTitle="Platform Name"
                  metricRows={searcher?.search(filters.advancedSearch) || []}
                  setSelectedItems={setSelectedPlatforms}
                  selectedItems={tableState.selectedPlatforms}
                />
              )}
            </>
          )}
          {tabState.tabView === "campaign" && (
            <>
              {byCampaignLoading && (
                <div className="h-[600px]">
                  <img
                    src={RegularGraphic}
                    alt="Loading screen gif"
                    className={clsx("w-1/2 m-auto")}
                  />
                </div>
              )}
              {byCampaignData && (
                <TabTable
                  startingColumnTitle="Campaign Name"
                  metricRows={searcher?.search(filters.advancedSearch) || []}
                  setSelectedItems={setSelectedCampaigns}
                  selectedItems={tableState.selectedCampaigns}
                />
              )}
            </>
          )}
          {tabState.tabView === "ad" && (
            <>
              {byAdLoading && (
                <div className="h-[600px]">
                  <img
                    src={RegularGraphic}
                    alt="Loading screen gif"
                    className={clsx("w-1/2 m-auto")}
                  />
                </div>
              )}
              {byAdData && (
                <TabTable
                  startingColumnTitle="Ad Name"
                  metricRows={searcher?.search(filters.advancedSearch) || []}
                  setSelectedItems={setSelectedAds}
                  selectedItems={tableState.selectedAds}
                />
              )}
            </>
          )}
        </div>
      </div>
      <FeedbackForm formId="4ef539b7-33ec-4cec-9691-72f004220fd7" />
      <FilterModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
    </div>
  );
}
